import { colors, typography, layout } from '../../../styles/theme';
import { getContrastTextColor, withAlpha } from '../../utils/sharedBranding';

/** Acento de filtros: Terrio por defecto, color de marca en vista compartida branded. */
export const FILTER_ACCENT = 'var(--filter-accent)';
export const FILTER_ACCENT_CONTRAST = 'var(--filter-accent-contrast)';
export const FILTER_ACCENT_10 = 'var(--filter-accent-10)';
export const FILTER_ACCENT_14 = 'var(--filter-accent-14)';
export const FILTER_ACCENT_20 = 'var(--filter-accent-20)';
export const FILTER_ACCENT_27 = 'var(--filter-accent-27)';
export const FILTER_ACCENT_33 = 'var(--filter-accent-33)';
export const FILTER_ACCENT_35 = 'var(--filter-accent-35)';
export const FILTER_ACCENT_40 = 'var(--filter-accent-40)';
export const FILTER_ACCENT_67 = 'var(--filter-accent-67)';

/**
 * Variables CSS del acento. Aplicar en el contenedor de búsqueda/filtros
 * para que el panel y sus hijos hereden el color (marca o Terrio).
 * @param {string|null|undefined} accentColor
 */
export function filterAccentCssVars(accentColor) {
  const accent = accentColor || colors.primary;
  // Con el acento de Terrio se conserva el blanco del design system. Solo se
  // calcula para colores de marca, donde un acento claro dejaría texto ilegible.
  const contrast = accentColor ? getContrastTextColor(accent) : colors.textPanelDescription;
  return {
    '--filter-accent': accent,
    '--filter-accent-contrast': contrast,
    '--filter-accent-10': withAlpha(accent, 0.1),
    '--filter-accent-14': withAlpha(accent, 0.14),
    '--filter-accent-20': withAlpha(accent, 0.2),
    '--filter-accent-27': withAlpha(accent, 0.27),
    '--filter-accent-33': withAlpha(accent, 0.33),
    '--filter-accent-35': withAlpha(accent, 0.35),
    '--filter-accent-40': withAlpha(accent, 0.4),
    '--filter-accent-67': withAlpha(accent, 0.67),
  };
}

export const FILTER_PANEL_RADIUS = '8px';
export const FILTER_DROPDOWN_HEIGHT = 40;
/** Margen derecho del panel lateral (desktop). */
export const FILTER_PANEL_RIGHT_OFFSET = 16;
/** Hueco bajo el header. */
const FILTER_PANEL_TOP_GAP = 8;
/** Hueco sobre el borde inferior del viewport. */
const FILTER_PANEL_BOTTOM_GAP = 8;
/** Ancho mínimo cuando el panel se ancla al botón Filtros. */
export const FILTER_PANEL_MIN_WIDTH = 280;
/** Línea divisoria bajo el título (elevated border del design system). */
export const FILTER_PANEL_DIVIDER = '#383838';
const FILTER_PANEL_PADDING_X = 16;

export const filterPanelStyle = {
  backgroundColor: colors.backgroundDark,
  borderRadius: FILTER_PANEL_RADIUS,
  border: `1px solid ${colors.border}`,
  boxShadow: '0 8px 32px rgba(0,0,0,0.55)',
  // Sin padding horizontal: el scroll recorta márgenes negativos (overflow-y: auto
  // fuerza overflow-x distinto de visible). El gutter va en el contenido.
  padding: '14px 0 12px',
  boxSizing: 'border-box',
  animation: 'filterPanelIn 0.18s ease-out',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

/** Padding lateral del contenido; los divisores quedan fuera y llegan al borde. */
export const filterPanelGutterStyle = {
  paddingLeft: `${FILTER_PANEL_PADDING_X}px`,
  paddingRight: `${FILTER_PANEL_PADDING_X}px`,
  boxSizing: 'border-box',
};

/** Panel fijo a la derecha; el borde izquierdo se alinea al botón Filtros del header. */
export const filterPanelSidebarPositionStyle = ({
  isMobile = false,
  anchorLeft = null,
  bottomOffset = 0,
} = {}) => {
  const top = `${layout.mapHeaderHeight + FILTER_PANEL_TOP_GAP}px`;
  // `bottom` (no height/100vh): cubre zoom y no se sale, también con footer branded.
  const bottom = `${FILTER_PANEL_BOTTOM_GAP + Math.max(0, Number(bottomOffset) || 0)}px`;

  if (isMobile) {
    return {
      position: 'fixed',
      top,
      right: `${FILTER_PANEL_RIGHT_OFFSET}px`,
      left: `${FILTER_PANEL_RIGHT_OFFSET}px`,
      bottom,
      zIndex: 1200,
    };
  }

  if (anchorLeft != null) {
    return {
      position: 'fixed',
      top,
      left: `${anchorLeft}px`,
      right: `${FILTER_PANEL_RIGHT_OFFSET}px`,
      minWidth: `${FILTER_PANEL_MIN_WIDTH}px`,
      bottom,
      zIndex: 1200,
    };
  }

  return {
    position: 'fixed',
    top,
    right: `${FILTER_PANEL_RIGHT_OFFSET}px`,
    minWidth: `${FILTER_PANEL_MIN_WIDTH}px`,
    bottom,
    zIndex: 1200,
  };
};

/** Chips sticky bajo el título (fuera del scroll del body). */
export const filterAppliedChipsStickyStyle = {
  flexShrink: 0,
  paddingBottom: '12px',
  paddingLeft: `${FILTER_PANEL_PADDING_X}px`,
  paddingRight: `${FILTER_PANEL_PADDING_X}px`,
};

/** Bloque sticky: chips + alcance, para no perder qué se está filtrando. */
export const filterStickyTopStyle = {
  flexShrink: 0,
  zIndex: 2,
  backgroundColor: colors.backgroundDark,
  boxShadow: '0 6px 12px -8px rgba(0, 0, 0, 0.55)',
};

export const filterScopeStickyStyle = {
  paddingLeft: `${FILTER_PANEL_PADDING_X}px`,
  paddingRight: `${FILTER_PANEL_PADDING_X}px`,
  paddingBottom: '12px',
  boxSizing: 'border-box',
};

/** Tabs Lote / Unidades a ancho completo, como el DockPanel. */
export const filterTabsStickyStyle = {
  flexShrink: 0,
  width: '100%',
  boxSizing: 'border-box',
};

/** Mismo patrón que las tabs del detalle de lote: texto + indicador, no botón. */
export const filterPanelTabsSx = {
  minHeight: 40,
  borderBottom: `1px solid ${FILTER_PANEL_DIVIDER}`,
  '& .MuiTab-root': {
    color: colors.textSecondary,
    textTransform: 'none',
    fontFamily: typography.fontFamily.primary,
    fontSize: '16px',
    lineHeight: '24px',
    minHeight: 40,
    paddingTop: 0,
    paddingBottom: 0,
  },
  '& .MuiTab-root.Mui-selected': { color: FILTER_ACCENT },
  '& .MuiTabs-indicator': { backgroundColor: FILTER_ACCENT, height: 3 },
};

export const filterPanelBodyScrollClassName = 'filter-panel-scroll';

export const filterPanelBodyScrollStyle = {
  width: '100%',
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
};

export const filterPanelHeaderStyle = {
  flexShrink: 0,
  marginBottom: '12px',
  paddingLeft: `${FILTER_PANEL_PADDING_X}px`,
  paddingRight: `${FILTER_PANEL_PADDING_X}px`,
};

export const filterPanelHeaderRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  paddingBottom: '12px',
};

export const filterPanelTitleStyle = {
  fontFamily: typography.fontFamily.primary,
  fontSize: '16px',
  fontWeight: typography.fontWeight.semibold,
  lineHeight: typography.lineHeight.normal,
  letterSpacing: '0.1px',
  textTransform: 'uppercase',
  color: colors.textPanelDescription,
  margin: 0,
};

export const filterPanelTitleDividerStyle = {
  width: `calc(100% + ${FILTER_PANEL_PADDING_X * 2}px)`,
  marginLeft: `-${FILTER_PANEL_PADDING_X}px`,
  height: '1px',
  backgroundColor: FILTER_PANEL_DIVIDER,
};

export const filterDropdownsRowStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '10px',
  width: '100%',
};

/** Columna única para todos los dropdowns (layout vertical). */
export const filterDropdownsColumnStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  width: '100%',
};

/** @deprecated usar filterDropdownsColumnStyle */
export const filterDropdownsGridStyle = (columns = 1) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
  gap: '10px',
  width: '100%',
});

/**
 * Separador entre secciones. El aire vertical lo pone el header del grupo
 * para que el título quede centrado entre líneas.
 */
export const filterGroupDividerStyle = {
  borderTop: `1px solid ${FILTER_PANEL_DIVIDER}`,
};

export const filterSectionLabelStyle = {
  marginTop: '4px',
  marginBottom: '8px',
  fontSize: typography.fontSize.xs,
  fontFamily: typography.fontFamily.primary,
  fontWeight: typography.fontWeight.semibold,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: colors.textSecondary,
};

export const filterShowOnlyRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
  marginTop: '12px',
  paddingTop: '12px',
  paddingLeft: `${FILTER_PANEL_PADDING_X}px`,
  paddingRight: `${FILTER_PANEL_PADDING_X}px`,
  boxSizing: 'border-box',
  borderTop: `1px solid ${FILTER_PANEL_DIVIDER}`,
};

export const filterShowOnlyTextBlockStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  minWidth: 0,
};

export const filterShowOnlyTitleStyle = (disabled = false) => ({
  fontSize: typography.fontSize.sm,
  fontFamily: typography.fontFamily.primary,
  fontWeight: typography.fontWeight.medium,
  lineHeight: typography.lineHeight.normal,
  color: disabled ? colors.textSecondary : 'rgba(255,255,255,0.78)',
});

export const filterShowOnlyDescriptionStyle = (disabled = false) => ({
  fontSize: typography.fontSize.xs,
  fontFamily: typography.fontFamily.primary,
  fontWeight: typography.fontWeight.regular,
  lineHeight: typography.lineHeight.tight,
  color: disabled ? colors.textSecondary : 'rgba(255,255,255,0.42)',
});

export const filterShowOnlyToggleStyle = ({ active = false, disabled = false } = {}) => ({
  width: '40px',
  height: '22px',
  borderRadius: '999px',
  border: `1px solid ${active && !disabled ? FILTER_ACCENT_67 : colors.border}`,
  backgroundColor: active && !disabled ? FILTER_ACCENT_33 : 'rgba(255,255,255,0.06)',
  padding: '2px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: active && !disabled ? 'flex-end' : 'flex-start',
  cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.45 : 1,
  transition: 'all 0.15s ease',
  flexShrink: 0,
});

export const filterShowOnlyToggleKnobStyle = ({ active = false, disabled = false } = {}) => ({
  width: '16px',
  height: '16px',
  borderRadius: '50%',
  backgroundColor: active && !disabled ? FILTER_ACCENT : 'rgba(255,255,255,0.65)',
  transition: 'all 0.15s ease',
});

export const filterPanelFooterStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  flexShrink: 0,
  gap: '10px',
  marginTop: '12px',
  paddingTop: '12px',
  paddingLeft: `${FILTER_PANEL_PADDING_X}px`,
  paddingRight: `${FILTER_PANEL_PADDING_X}px`,
  borderTop: `1px solid ${FILTER_PANEL_DIVIDER}`,
  backgroundColor: colors.backgroundDark,
};

export const filterPanelFooterActionsRowStyle = {
  display: 'flex',
  alignItems: 'stretch',
  gap: '10px',
  width: '100%',
};

export const filterClearLinkStyle = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '36px',
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  color: '#ABABAB',
  fontFamily: typography.fontFamily.primary,
  fontSize: '14px',
  fontWeight: typography.fontWeight.semibold,
  lineHeight: '16px',
  letterSpacing: '0.02em',
  textDecoration: 'underline',
  textUnderlineOffset: '3px',
};

export const filterApplyButtonClassName = 'filter-panel-apply-btn';

export const filterApplyButtonStyle = {
  flex: 1,
  minWidth: 0,
  height: '36px',
  padding: '0 12px',
  borderRadius: '6px',
  border: 'none',
  color: FILTER_ACCENT_CONTRAST,
  fontSize: typography.fontSize.sm,
  fontFamily: typography.fontFamily.primary,
  fontWeight: typography.fontWeight.medium,
  cursor: 'pointer',
  transition: 'background-color 0.15s ease',
  whiteSpace: 'nowrap',
};

/** Nota al pie del grupo Contexto y riesgo: cobertura desigual entre provincias. */
export const filterCoverageNoteStyle = {
  marginTop: '-6px',
  marginBottom: '4px',
  fontSize: typography.fontSize.xs,
  fontFamily: typography.fontFamily.primary,
  color: colors.textSecondary,
  lineHeight: 1.4,
  opacity: 0.75,
};

export const filterDisabledMessageStyle = {
  marginTop: '12px',
  fontSize: typography.fontSize.xs,
  color: colors.textSecondary,
  textAlign: 'center',
  lineHeight: 1.4,
};

export const filterPanelKeyframes = `
  @keyframes filterPanelIn {
    from { opacity: 0; transform: translateX(12px); }
    to { opacity: 1; transform: translateX(0); }
  }

  /* Scroll usable, sin barra visible (evita solapar chevrons / sliders). */
  .filter-panel-scroll {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .filter-panel-scroll::-webkit-scrollbar {
    width: 0;
    height: 0;
    display: none;
  }

  .filter-panel-apply-btn {
    background-color: ${FILTER_ACCENT};
  }

  .filter-panel-apply-btn:not(:disabled):hover {
    background-color: color-mix(in srgb, ${FILTER_ACCENT} 78%, black);
  }

  .filter-panel-apply-btn:disabled {
    background-color: ${FILTER_ACCENT_35};
    cursor: not-allowed;
  }
`;
